<template>
  <div class="modal fade" id="newEntryModal" tabindex="-1" role="dialog">
    <div class="modal-dialog modal-lg modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            <i class="mdi mdi-ticket text-success me-2"></i>
            New Park Entry Registration
          </h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
        </div>

        <form @submit.prevent="createEntry">
          <div class="modal-body">
            <!-- Entry Type Selection -->
            <div class="row mb-4">
              <div class="col-12">
                <label class="form-label fw-semibold">Select Entry Type</label>
                <div class="row g-3">
                  <div class="col-md-3" v-for="type in entryTypes" :key="type.value">
                    <div class="card border cursor-pointer entry-type-card"
                         :class="{'border-success bg-light': entryForm.entryType === type.value}"
                         @click="selectEntryType(type.value)">
                      <div class="card-body text-center p-3">
                        <i :class="type.icon + ' font-size-24 mb-2'"
                           :style="{color: entryForm.entryType === type.value ? '#198754' : '#6c757d'}"></i>
                        <h6 class="mb-1">{{ type.label }}</h6>
                        <small class="text-muted">{{ type.description }}</small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Visitor Information -->
            <div class="row mb-3">
              <div class="col-12">
                <h6 class="fw-semibold mb-3">Visitor Information</h6>
              </div>
            </div>

            <div class="row g-3 mb-3">
              <div class="col-md-6">
                <label class="form-label">Visitor Name / Organization *</label>
                <input type="text" class="form-control" v-model="entryForm.visitorName"
                       placeholder="Enter visitor name or organization" required>
              </div>
              <div class="col-md-6">
                <label class="form-label">ID / Passport Number *</label>
                <input type="text" class="form-control" v-model="entryForm.idNumber"
                       placeholder="Enter ID or passport number" required>
              </div>
            </div>

            <div class="row g-3 mb-3">
              <div class="col-md-6">
                <label class="form-label">Phone Number *</label>
                <div class="input-group">
                  <span class="input-group-text">+254</span>
                  <input type="tel" class="form-control" v-model="entryForm.phoneNumber"
                         placeholder="712345678" required pattern="[0-9]{9}">
                </div>
              </div>
              <div class="col-md-6">
                <label class="form-label">Email Address</label>
                <input type="email" class="form-control" v-model="entryForm.email"
                       placeholder="visitor@example.com">
              </div>
            </div>

            <div class="row g-3 mb-3">
              <div class="col-md-6">
                <label class="form-label">Country of Origin *</label>
                <select class="form-select" v-model="entryForm.country" @change="updateFees()" required>
                  <option value="">Select Country</option>
                  <option value="KE">Kenya</option>
                  <option value="TZ">Tanzania</option>
                  <option value="UG">Uganda</option>
                  <option value="RW">Rwanda</option>
                  <option value="US">United States</option>
                  <option value="GB">United Kingdom</option>
                  <option value="DE">Germany</option>
                  <option value="FR">France</option>
                  <option value="CN">China</option>
                  <option value="OTHER">Other</option>
                </select>
              </div>
              <div class="col-md-6">
                <label class="form-label">Entry Gate *</label>
                <select class="form-select" v-model="entryForm.entryGate" required>
                  <option value="">Select Gate</option>
                  <option value="Kimana Gate">Kimana Gate</option>
                  <option value="Meshanani Gate">Meshanani Gate</option>
                </select>
              </div>
            </div>

            <!-- Visitor Count -->
            <div class="row mb-3">
              <div class="col-12">
                <h6 class="fw-semibold mb-3">Number of Visitors</h6>
              </div>
            </div>

            <div class="row g-3 mb-3">
              <div class="col-md-4">
                <label class="form-label">Adults (16+ years) *</label>
                <input type="number" class="form-control" v-model.number="entryForm.adults"
                       @change="calculateFees()" min="0" required>
              </div>
              <div class="col-md-4">
                <label class="form-label">Children (3-15 years)</label>
                <input type="number" class="form-control" v-model.number="entryForm.children"
                       @change="calculateFees()" min="0">
              </div>
              <div class="col-md-4">
                <label class="form-label">Total Visitors</label>
                <input type="text" class="form-control" :value="totalVisitors" readonly>
              </div>
            </div>

            <!-- Vehicle Information -->
            <div class="row mb-3">
              <div class="col-12">
                <div class="form-check">
                  <input class="form-check-input" type="checkbox" v-model="entryForm.hasVehicle"
                         @change="calculateFees()" id="hasVehicle">
                  <label class="form-check-label fw-semibold" for="hasVehicle">
                    Visitor has vehicle
                  </label>
                </div>
              </div>
            </div>

            <div v-if="entryForm.hasVehicle" class="row g-3 mb-3">
              <div class="col-md-4">
                <label class="form-label">Vehicle Registration *</label>
                <input type="text" class="form-control" v-model="entryForm.vehicleReg"
                       placeholder="KAA 123B" required>
              </div>
              <div class="col-md-4">
                <label class="form-label">Vehicle Type *</label>
                <select class="form-select" v-model="entryForm.vehicleType" @change="calculateFees()" required>
                  <option value="">Select Type</option>
                  <option value="Personal Car">Personal Car</option>
                  <option value="Safari Van">Safari Van (6-8 seats)</option>
                  <option value="Tour Bus">Tour Bus (25+ seats)</option>
                  <option value="School Bus">School Bus</option>
                </select>
              </div>
              <div class="col-md-4">
                <label class="form-label">Driver Name</label>
                <input type="text" class="form-control" v-model="entryForm.driverName"
                       placeholder="Driver name">
              </div>
            </div>

            <!-- Special Permits/Activities -->
            <div class="row mb-3">
              <div class="col-12">
                <h6 class="fw-semibold mb-3">Special Activities</h6>
              </div>
            </div>

            <div class="row g-3 mb-3">
              <div class="col-md-6">
                <div class="form-check">
                  <input class="form-check-input" type="checkbox" v-model="entryForm.activities.photography"
                         @change="calculateFees()" id="photography">
                  <label class="form-check-label" for="photography">
                    <i class="mdi mdi-camera text-primary me-2"></i>
                    Professional Photography (+{{ formatPrice(5000) }})
                  </label>
                </div>
                <div class="form-check">
                  <input class="form-check-input" type="checkbox" v-model="entryForm.activities.filming"
                         @change="calculateFees()" id="filming">
                  <label class="form-check-label" for="filming">
                    <i class="mdi mdi-video text-danger me-2"></i>
                    Commercial Filming (+{{ formatPrice(15000) }})
                  </label>
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-check">
                  <input class="form-check-input" type="checkbox" v-model="entryForm.activities.camping"
                         @change="calculateFees()" id="camping">
                  <label class="form-check-label" for="camping">
                    <i class="mdi mdi-tent text-success me-2"></i>
                    Camping (+{{ formatPrice(2000) }}/night)
                  </label>
                </div>
                <div class="form-check">
                  <input class="form-check-input" type="checkbox" v-model="entryForm.activities.research"
                         @change="calculateFees()" id="research">
                  <label class="form-check-label" for="research">
                    <i class="mdi mdi-flask text-info me-2"></i>
                    Research Permit (Special Rate)
                  </label>
                </div>
              </div>
            </div>

            <!-- Stay Duration -->
            <div class="row g-3 mb-3">
              <div class="col-md-6">
                <label class="form-label">Expected Duration</label>
                <select class="form-select" v-model="entryForm.duration" @change="calculateFees()">
                  <option value="DAY">Day Visit</option>
                  <option value="OVERNIGHT">Overnight (1 night)</option>
                  <option value="MULTI_DAY">Multiple Days</option>
                </select>
              </div>
              <div class="col-md-6" v-if="entryForm.duration === 'MULTI_DAY'">
                <label class="form-label">Number of Nights</label>
                <input type="number" class="form-control" v-model.number="entryForm.nights"
                       @change="calculateFees()" min="2" placeholder="2">
              </div>
            </div>

            <!-- Payment Method -->
            <div class="row mb-3">
              <div class="col-12">
                <h6 class="fw-semibold mb-3">Payment Information</h6>
              </div>
            </div>

            <div class="row g-3 mb-3">
              <div class="col-md-6">
                <label class="form-label">Payment Method *</label>
                <select class="form-select" v-model="entryForm.paymentMethod" required>
                  <option value="">Select Payment Method</option>
                  <option value="Cash">Cash</option>
                  <option value="M-Pesa">M-Pesa</option>
                  <option value="Credit Card">Credit Card</option>
                  <option value="Bank Transfer">Bank Transfer</option>
                  <option value="Invoice">Invoice (Corporate)</option>
                </select>
              </div>
              <div class="col-md-6" v-if="entryForm.paymentMethod === 'M-Pesa'">
                <label class="form-label">M-Pesa Transaction Code</label>
                <input type="text" class="form-control" v-model="entryForm.mpesaCode"
                       placeholder="QEF3R5TY8U">
              </div>
            </div>

            <!-- Fee Breakdown -->
            <div class="row mb-3">
              <div class="col-12">
                <div class="card bg-light">
                  <div class="card-header">
                    <h6 class="mb-0">Fee Breakdown</h6>
                  </div>
                  <div class="card-body">
                    <table class="table table-sm mb-0">
                      <tbody>
                      <tr v-if="feeBreakdown.adultFees > 0">
                        <td>Park Entry - Adults ({{ entryForm.adults }} × {{ formatPrice(getUnitFee('adult')) }})</td>
                        <td class="text-end">{{ formatPrice(feeBreakdown.adultFees) }}</td>
                      </tr>
                      <tr v-if="feeBreakdown.childFees > 0">
                        <td>Park Entry - Children ({{ entryForm.children }} × {{ formatPrice(getUnitFee('child')) }})</td>
                        <td class="text-end">{{ formatPrice(feeBreakdown.childFees) }}</td>
                      </tr>
                      <tr v-if="feeBreakdown.vehicleFees > 0">
                        <td>Vehicle Entry ({{ entryForm.vehicleType }})</td>
                        <td class="text-end">{{ formatPrice(feeBreakdown.vehicleFees) }}</td>
                      </tr>
                      <tr v-if="feeBreakdown.activityFees > 0">
                        <td>Special Activities</td>
                        <td class="text-end">{{ formatPrice(feeBreakdown.activityFees) }}</td>
                      </tr>
                      <tr v-if="entryForm.duration !== 'DAY' && feeBreakdown.totalFees > 0">
                        <td>Duration Multiplier ({{ getDurationMultiplier() }})</td>
                        <td class="text-end">-</td>
                      </tr>
                      <tr class="table-active">
                        <td><strong>Total Amount Due</strong></td>
                        <td class="text-end"><strong class="text-success">{{ formatPrice(feeBreakdown.totalFees) }}</strong></td>
                      </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            <!-- Additional Notes -->
            <div class="row">
              <div class="col-12">
                <label class="form-label">Additional Notes</label>
                <textarea class="form-control" v-model="entryForm.notes"
                          placeholder="Any special requirements or notes..." rows="2"></textarea>
              </div>
            </div>
          </div>

          <div class="modal-footer">
            <div class="d-flex justify-content-between w-100">
              <div class="text-muted">
                <small>* Required fields</small>
              </div>
              <div>
                <button type="button" class="btn btn-secondary me-2" data-bs-dismiss="modal">Cancel</button>
                <button type="submit" class="btn btn-success" :disabled="loading || feeBreakdown.totalFees === 0">
                  <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status"></span>
                  <i v-else class="mdi mdi-check me-2"></i>
                  {{ loading ? 'Processing...' : 'Create Entry ('+formatPrice(feeBreakdown.totalFees)+')' }}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
export default {
  name: "NewEntryModal",
  props: {
    feeStructure: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      entryForm: {
        entryType: 'CITIZEN',
        visitorName: '',
        idNumber: '',
        phoneNumber: '',
        email: '',
        country: 'KE',
        entryGate: '',
        adults: 1,
        children: 0,
        hasVehicle: false,
        vehicleReg: '',
        vehicleType: '',
        driverName: '',
        activities: {
          photography: false,
          filming: false,
          camping: false,
          research: false
        },
        duration: 'DAY',
        nights: 2,
        paymentMethod: '',
        mpesaCode: '',
        notes: ''
      },

      entryTypes: [
        {
          value: 'CITIZEN',
          label: 'Citizen',
          description: 'Kenyan Citizens',
          icon: 'mdi mdi-account-check'
        },
        {
          value: 'RESIDENT',
          label: 'Resident',
          description: 'East Africa',
          icon: 'mdi mdi-home-account'
        },
        {
          value: 'NON_RESIDENT',
          label: 'Non-Resident',
          description: 'International',
          icon: 'mdi mdi-airplane'
        },
        {
          value: 'STUDENT',
          label: 'Student',
          description: 'Educational',
          icon: 'mdi mdi-school'
        }
      ],

      feeBreakdown: {
        adultFees: 0,
        childFees: 0,
        vehicleFees: 0,
        activityFees: 0,
        totalFees: 0
      }
    }
  },
  computed: {
    totalVisitors() {
      return this.entryForm.adults + this.entryForm.children;
    }
  },
  methods: {
    formatPrice(value) {
      return new Intl.NumberFormat('en-KE', {
        style: 'currency',
        currency: 'KES',
        minimumFractionDigits: 0
      }).format(value);
    },

    selectEntryType(type) {
      this.entryForm.entryType = type;
      this.updateFees();
    },

    updateFees() {
      // Update entry type based on country for non-Kenyans
      if (this.entryForm.country !== 'KE' && this.entryForm.entryType === 'CITIZEN') {
        if (['TZ', 'UG', 'RW'].includes(this.entryForm.country)) {
          this.entryForm.entryType = 'RESIDENT';
        } else {
          this.entryForm.entryType = 'NON_RESIDENT';
        }
      }
      this.calculateFees();
    },

    getUnitFee(ageGroup) {
      const fees = this.feeStructure.entry.find(f => f.category === this.getEntryCategory());
      return fees ? fees[ageGroup] : 0;
    },

    getEntryCategory() {
      const categoryMap = {
        'CITIZEN': 'Citizen',
        'RESIDENT': 'Resident',
        'NON_RESIDENT': 'Non-Resident',
        'STUDENT': 'Student'
      };
      return categoryMap[this.entryForm.entryType] || 'Citizen';
    },

    getVehicleFee() {
      if (!this.entryForm.hasVehicle || !this.entryForm.vehicleType) return 0;
      const vehicle = this.feeStructure.vehicle.find(v => v.type === this.entryForm.vehicleType);
      return vehicle ? vehicle.amount : 0;
    },

    getDurationMultiplier() {
      if (this.entryForm.duration === 'DAY') return 1;
      if (this.entryForm.duration === 'OVERNIGHT') return 2;
      if (this.entryForm.duration === 'MULTI_DAY') return this.entryForm.nights || 2;
      return 1;
    },

    calculateFees() {
      // Calculate adult fees
      this.feeBreakdown.adultFees = this.entryForm.adults * this.getUnitFee('adult');

      // Calculate child fees
      this.feeBreakdown.childFees = this.entryForm.children * this.getUnitFee('child');

      // Calculate vehicle fees
      this.feeBreakdown.vehicleFees = this.getVehicleFee();

      // Calculate activity fees
      let activityFees = 0;
      if (this.entryForm.activities.photography) activityFees += 5000;
      if (this.entryForm.activities.filming) activityFees += 15000;
      if (this.entryForm.activities.camping) activityFees += 2000 * this.getDurationMultiplier();
      if (this.entryForm.activities.research) {
        // Research gets 50% discount
        activityFees = (this.feeBreakdown.adultFees + this.feeBreakdown.childFees) * 0.5;
      }
      this.feeBreakdown.activityFees = activityFees;

      // Calculate total with duration multiplier
      const baseFees = this.feeBreakdown.adultFees + this.feeBreakdown.childFees;
      const multipliedFees = baseFees * this.getDurationMultiplier();

      this.feeBreakdown.totalFees = multipliedFees + this.feeBreakdown.vehicleFees + this.feeBreakdown.activityFees;

      // Special case for research
      if (this.entryForm.activities.research) {
        this.feeBreakdown.totalFees = this.feeBreakdown.activityFees + this.feeBreakdown.vehicleFees;
      }
    },

    generateTicketNumber() {
      const date = new Date();
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const sequence = String(Math.floor(Math.random() * 10000)).padStart(5, '0');
      return `AMB-${year}${month}${day}${sequence}`;
    },

    async createEntry() {
      try {
        this.loading = true;

        // Validate minimum requirements
        if (this.totalVisitors === 0) {
          alert('Please specify at least one visitor');
          return;
        }

        if (this.entryForm.hasVehicle && !this.entryForm.vehicleReg) {
          alert('Please enter vehicle registration number');
          return;
        }

        // Simulate API call
        const entryData = {
          ticketNumber: this.generateTicketNumber(),
          visitorName: this.entryForm.visitorName,
          idNumber: this.entryForm.idNumber,
          phoneNumber: '+254' + this.entryForm.phoneNumber,
          email: this.entryForm.email,
          entryType: this.entryForm.entryType,
          visitorCount: this.totalVisitors,
          adults: this.entryForm.adults,
          children: this.entryForm.children,
          country: this.entryForm.country,
          vehicleReg: this.entryForm.vehicleReg || null,
          vehicleType: this.entryForm.vehicleType || null,
          driverName: this.entryForm.driverName || null,
          entryTime: new Date().toISOString(),
          entryGate: this.entryForm.entryGate,
          parkFees: this.feeBreakdown.adultFees + this.feeBreakdown.childFees + this.feeBreakdown.activityFees,
          vehicleFees: this.feeBreakdown.vehicleFees,
          totalFees: this.feeBreakdown.totalFees,
          paymentMethod: this.entryForm.paymentMethod,
          mpesaCode: this.entryForm.mpesaCode || null,
          status: 'IN_PARK',
          duration: null,
          exitTime: null,
          exitGate: null,
          activities: { ...this.entryForm.activities },
          stayDuration: this.entryForm.duration,
          nights: this.entryForm.nights,
          notes: this.entryForm.notes
        };

        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 2000));

        this.$emit('entry-created', entryData);

        // Print ticket automatically
        if (confirm('Entry created successfully! Print ticket now?')) {
          window.print();
        }

        // Reset form
        this.resetForm();

        // Close modal
        const modal = bootstrap.Modal.getInstance(document.getElementById('newEntryModal'));
        modal.hide();

      } catch (error) {
        console.error('Error creating entry:', error);
        alert('Failed to create entry. Please try again.');
      } finally {
        this.loading = false;
      }
    },

    resetForm() {
      this.entryForm = {
        entryType: 'CITIZEN',
        visitorName: '',
        idNumber: '',
        phoneNumber: '',
        email: '',
        country: 'KE',
        entryGate: '',
        adults: 1,
        children: 0,
        hasVehicle: false,
        vehicleReg: '',
        vehicleType: '',
        driverName: '',
        activities: {
          photography: false,
          filming: false,
          camping: false,
          research: false
        },
        duration: 'DAY',
        nights: 2,
        paymentMethod: '',
        mpesaCode: '',
        notes: ''
      };
      this.calculateFees();
    }
  },
  mounted() {
    this.calculateFees();
  }
}
</script>

<style scoped>
.entry-type-card {
  cursor: pointer;
  transition: all 0.3s ease;
}

.entry-type-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

.cursor-pointer {
  cursor: pointer;
}

.form-check-label {
  cursor: pointer;
}

.card {
  border-radius: 8px;
}

.modal-dialog {
  max-width: 900px;
}

.bg-light {
  background-color: #f8f9fa !important;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}

.table-active {
  background-color: #f0f0f0;
}

.font-size-24 {
  font-size: 24px;
}
</style>